"use client"

import PropTypes from "prop-types"
import { useTheme } from "@mui/material/styles"
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Typography,
  Paper,
  IconButton,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Checkbox,
  DialogActions,
  TextField,
  Pagination,
  Stack,
  Divider,
} from "@mui/material"
import DeleteIcon from "@mui/icons-material/Delete"
import SearchIcon from "@mui/icons-material/Search"
import GroupIcon from "@mui/icons-material/Group"
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings"
import CameraAltIcon from "@mui/icons-material/CameraAlt"
import PersonAddIcon from "@mui/icons-material/PersonAdd"
import formatName from "reuseableTable/naming-convention"
import axios from "axios"
import { useDispatch } from "react-redux"
import useRequestIdGenerater from "customhooks"
import useTimeStamp from "reuseableTable/uniquetimestamp/GenerateUniqueTimeStamp"
import { useEffect, useState, Fragment } from "react"
import { openSnackbar } from "store/slices/snackbar"
import UploadProfileAndCoverImage from "views/group/UploadProfileAndCoverImage"

const UserDetails = ({
  user,
  connections,
  getContactList,
  setUser,
  fetchGroupMembers,
  groupMembers = [],
  groupDetailId,
  isAdmin = true,
  fetchData,
  setOpenChatDrawer,
  setCollapsed
}) => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const RequestId = useRequestIdGenerater()
  const { rendomRequestId, generateTimeStamp } = useTimeStamp()

  const [connectionList, setConnectionList] = useState([])
  const [selectedUsers, setSelectedUsers] = useState([])
  const [groupModalOpen, setGroupModalOpen] = useState(false)
  const [loading, setIsLoading] = useState(false)
  const [allConnections, setAllConnections] = useState([])
  const [pageCache, setPageCache] = useState({})
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [searchTerm, setSearchTerm] = useState("")
  const [openProfileUpdate, setOpenProfileUpdate] = useState(false)
  const [filePathName, setFilePathName] = useState("")
  const [presignedImage, setPresignedImage] = useState("")

  const loggedInUserId = localStorage.getItem("userId")
  const isUserAdmin = groupMembers?.some(
    (member) => member.admin && member?.alumniMemberMini?.userId === loggedInUserId,
  )

  const handleOpenUpdateProfile = () => {
    setOpenProfileUpdate(true)
  }

  const handleCloseUpdateProfile = () => {
    setOpenProfileUpdate(false)
  }

  const getInitial = (name) => name?.charAt(0)?.toUpperCase() || "?"

  const stringToColor = (str) => {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash)
    }
    const h = hash % 360
    return `hsl(${h}, 70%, 80%)`
  }

  const handleAddMembers = async () => {
    const payload = selectedUsers.map((id) => ({ alumniMemberId: id }))
    try {
      await axios.post(`/v1/communication/group/add/group/member?groupId=${user?.id}`, payload, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("serviceToken")}`,
          "Content-Type": "application/json",
          requestId: RequestId(
            Math.floor(1000 + Math.random() * 9999),
            Math.floor(1000 + Math.random() * 9999 + rendomRequestId),
          ),
        },
      })
      dispatch(
        openSnackbar({
          open: true,
          message: "Members Added Successfully",
          variant: "alert",
          alert: { color: "success" },
          close: false,
        }),
      )
      setGroupModalOpen(false)
      setSelectedUsers([])
      fetchGroupMembers(user?.id)
      generateTimeStamp()
    } catch (error) {
      generateTimeStamp()
      dispatch(
        openSnackbar({
          open: true,
          message: "Failed to add members",
          variant: "alert",
          alert: { color: "error" },
          close: false,
        }),
      )
    }
  }

  const handleRemoveMember = (memberId) => {
    axios
      .post(
        `/v1/communication/group/remove/group/member?groupId=${user?.id}&memberId=${memberId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("serviceToken")}`,
            "Content-Type": "application/json",
            requestId: RequestId(
              Math.floor(1000 + Math.random() * 9999),
              Math.floor(1000 + Math.random() * 9999) + rendomRequestId,
            ),
          },
        },
      )
      .then(() => {
        dispatch(
          openSnackbar({
            open: true,
            message: "Member Removed Successfully",
            variant: "alert",
            alert: { color: "success" },
            close: false,
          }),
        )
        fetchGroupMembers(user?.id)
        generateTimeStamp()
      })
      .catch((error) => {
        const errorMessage = error.response?.status === 404 ? error.response.data.Msg : "Failed to remove member"
        dispatch(
          openSnackbar({
            open: true,
            message: errorMessage,
            variant: "alert",
            alert: { color: "error" },
            close: false,
          }),
        )
        generateTimeStamp()
      })
  }

  const handleMakeAdmin = (memberId) => {
    axios
      .put(
        `/v1/communication/group/make-admin?groupId=${user?.id}&alumniUserId=${memberId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("serviceToken")}`,
            "Content-Type": "application/json",
            requestId: RequestId(
              Math.floor(1000 + Math.random() * 9999),
              Math.floor(1000 + Math.random() * 9999) + rendomRequestId,
            ),
          },
        },
      )
      .then(() => {
        dispatch(
          openSnackbar({
            open: true,
            message: "Admin Rights Granted Successfully",
            variant: "alert",
            alert: { color: "success" },
            close: false,
          }),
        )
        fetchGroupMembers(user?.id)
        generateTimeStamp()
      })
      .catch((error) => {
        const errorMessage = error.response?.status === 404 ? error.response.data.Msg : "Failed to grant admin rights"
        dispatch(
          openSnackbar({
            open: true,
            message: errorMessage,
            variant: "alert",
            alert: { color: "error" },
            close: false,
          }),
        )
        generateTimeStamp()
      })
  }

  const handleUpdateGroupProfile = (filePathName) => {
    if (filePathName === "") return

    const payload = {
      groupId: user?.id,
      filePath: filePathName,
    }

    axios
      .post(`/v1/communication/group/update-icon`, payload, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("serviceToken")}`,
          "Content-Type": "application/json",
          requestId: RequestId(
            Math.floor(1000 + Math.random() * 9999),
            Math.floor(1000 + Math.random() * 9999) + rendomRequestId,
          ),
        },
      })
      .then((response) => {
        dispatch(
          openSnackbar({
            open: true,
            message: "Group Icon Updated Successfully",
            variant: "alert",
            alert: { color: "success" },
            close: false,
          }),
        )
        getContactList()
        const updatedGroupIcon = response?.data?.data?.groupIcon
        if (updatedGroupIcon) {
          setUser((prevUser) => ({
            ...prevUser,
            image: updatedGroupIcon,
          }))
        }
        generateTimeStamp()
      })
      .catch((error) => {
        const errorMessage = error.response?.status === 404 ? error.response.data.Msg : "Failed to update group icon"
        dispatch(
          openSnackbar({
            open: true,
            message: errorMessage,
            variant: "alert",
            alert: { color: "error" },
            close: false,
          }),
        )
        generateTimeStamp()
      })
  }

  const getConnectionList = async (currentPage = 1, size = rowsPerPage) => {
    setIsLoading(true)
    try {
      const apiPage = currentPage - 1
      const response = await axios.get(`/v1/connection/list?pageSize=${size}&pageNumber=${apiPage}`, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("serviceToken")}`,
          "Content-Type": "application/json",
          requestId: RequestId(
            Math.floor(1000 + Math.random() * 9999),
            Math.floor(1000 + Math.random() * 9999 + rendomRequestId),
          ),
        },
      })
      const data = response?.data?.data?.content || []
      setConnectionList(data)
      setAllConnections(data)
      setTotalCount(response?.data?.data?.totalElements)
      setPageCache((prev) => ({
        ...prev,
        [currentPage]: data,
      }))
    } catch (error) {
      console.error("Failed to fetch data:", error)
    } finally {
      setIsLoading(false)
      generateTimeStamp()
    }
  }

  const handlePageChange = (event, newPage) => {
    setPage(newPage)
    getConnectionList(newPage, rowsPerPage)
  }

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value)
  }

  const filteredConnections = connectionList.filter((connection) =>
    connection?.friendAlumni?.name?.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  useEffect(() => {
    if (groupModalOpen) {
      setPage(1)
      getConnectionList(1, rowsPerPage)
    }
  }, [groupModalOpen])

  const toggleSelect = (userId) => {
    setSelectedUsers((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]))
  }

  const totalPages = Math.ceil(totalCount / rowsPerPage)

  const handleExitGroup = () => {
    axios
      .post(`/v1/communication/group/remove/group/member?groupId=${user?.id}&memberId=${localStorage.getItem('alumniId')}`, {}, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("serviceToken")}`,
          "Content-Type": "application/json",
          requestId: RequestId(
            Math.floor(1000 + Math.random() * 9999),
            Math.floor(1000 + Math.random() * 9999) + rendomRequestId,
          ),
        },
      })
      .then((response) => {
        dispatch(
          openSnackbar({
            open: true,
            message: "Group Icon Updated Successfully",
            variant: "alert",
            alert: { color: "success" },
            close: false,
          }),
        )
        getContactList()
        setCollapsed(false)
        setOpenChatDrawer(false)
        generateTimeStamp()
      })
      .catch((error) => {
        const errorMessage = error.response?.status === 404 ? error.response.data.Msg : "Failed to update group icon"
        dispatch(
          openSnackbar({
            open: true,
            message: errorMessage,
            variant: "alert",
            alert: { color: "error" },
            close: false,
          }),
        )
        generateTimeStamp()
      })
  }

  useEffect(() => {
    handleUpdateGroupProfile(filePathName)
  }, [filePathName])

  return (
    <Card
      sx={{
        width: 350,
        mx: "auto",
        boxShadow: 1,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      {/* Header Section */}
      <CardContent sx={{ textAlign: "center", py: 2 }}>
        <Box sx={{ position: "relative", display: "inline-block", mb: 1.5 }}>
          <Avatar
            alt={user?.connectionName}
            src={`data:image/*;base64, ${user?.image?.data}` ?? presignedImage}
            sx={{
              width: 70,
              height: 70,
              border: `1px solid ${theme.palette.divider}`,
            }}
          />
          {user?.connectionType === "Group" && (
            <IconButton
              size="small"
              onClick={handleOpenUpdateProfile}
              sx={{
                position: "absolute",
                bottom: -4,
                right: -4,
                bgcolor: theme.palette.grey[600],
                color: "white",
                width: 24,
                height: 24,
                "&:hover": { bgcolor: theme.palette.grey[700] },
              }}
            >
              <CameraAltIcon sx={{ fontSize: 12 }} />
            </IconButton>
          )}
        </Box>

        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
          {user?.connectionName}
        </Typography>
        {user?.connectionType === "Group" && (
          <>

            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}
            >
              <GroupIcon sx={{ fontSize: 16 }} />
              {groupMembers?.length || 0} members
            </Typography>
          </>
        )}
      </CardContent>

      {/* Admin Controls */}
      {isAdmin && isUserAdmin && user?.connectionType === "Group" && (
        <>
          <Divider />
          <Box sx={{ p: 1.5 }}>
            <Button
              onClick={() => setGroupModalOpen(true)}
              variant="outlined"
              size="small"
              startIcon={<PersonAddIcon />}
              fullWidth
              sx={{ textTransform: "none" }}
            >
              Add Members
            </Button>
            <Button
              onClick={() => handleExitGroup()}
              variant="outlined"
              size="small"
              startIcon={<PersonAddIcon />}
              fullWidth
              sx={{ textTransform: "none", mt: 1 }}
            >
              Exit Group
            </Button>
          </Box>
        </>
      )}

      {/* Group Members Section */}
      {user?.connectionType === "Group" && (
        <>
          <Divider />
          <Box sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Members
            </Typography>

            <Box sx={{ maxHeight: 250, overflowY: "auto" }}>
              <Stack spacing={1}>
                {groupMembers?.map((data, index) => {
                  const avatarData = data?.alumniMemberMini?.avatar?.data
                  const name = data?.alumniMemberMini?.name
                  const memberId = data?.alumniMemberId
                  const alumniUserId = data?.alumniMemberMini?.userId
                  const isMemberAdmin = data?.admin
                  const isLoggedInUser = loggedInUserId === alumniUserId

                  return (
                    <Paper
                      key={index}
                      variant="outlined"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 1,
                        bgcolor: theme.palette.background.paper,
                      }}
                    >
                      <Avatar
                        src={avatarData ? `data:image/*;base64, ${avatarData}` : undefined}
                        sx={{
                          width: 32,
                          height: 32,
                          mr: 1,
                          bgcolor: stringToColor(name),
                          fontSize: "0.8rem",
                        }}
                      >
                        {!avatarData && getInitial(name)}
                      </Avatar>

                      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                            {formatName(name)}
                          </Typography>
                          {isMemberAdmin && (
                            <AdminPanelSettingsIcon sx={{ fontSize: 12, color: theme.palette.text.secondary }} />
                          )}
                          {isLoggedInUser && (
                            <Typography variant="caption" color="textSecondary">
                              (You)
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="caption" color="textSecondary" noWrap>
                          {data?.alumniMemberMini?.email}
                        </Typography>
                      </Box>

                      {isAdmin && isUserAdmin && !isLoggedInUser && (
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveMember(memberId)}
                            sx={{ color: theme.palette.error.main }}
                          >
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                          {!isMemberAdmin && (
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => handleMakeAdmin(memberId)}
                              sx={{ fontSize: "0.7rem", minWidth: "auto", px: 0.5, textTransform: "none" }}
                            >
                              Mark Admin
                            </Button>
                          )}
                        </Box>
                      )}
                    </Paper>
                  )
                })}
              </Stack>
            </Box>
          </Box>
        </>
      )}

      {/* Add Members Dialog */}
      <Dialog open={groupModalOpen} onClose={() => setGroupModalOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>
          <Typography variant="h6">Add Members</Typography>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search connections..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, fontSize: 18 }} />,
              }}
            />
          </Box>

          <Box sx={{ maxHeight: 300, overflow: "auto" }}>
            <List dense>
              {loading ? (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                filteredConnections?.map((data) => {
                  const isExisting = user?.alumniIds?.includes(data?.friendAlumniId)
                  const isSelected = selectedUsers.includes(data?.friendAlumniId)

                  return (
                    <Fragment key={data.id}>
                      <ListItemButton
                        disabled={isExisting}
                        onClick={() => !isExisting && toggleSelect(data?.friendAlumniId)}
                        sx={{ py: 0.5 }}
                      >
                        <Checkbox edge="start" checked={isSelected} disabled={isExisting} size="small" />
                        <ListItemAvatar>
                          <Avatar
                            src={data?.image?.data ? `data:image/*;base64, ${data?.image?.data}` : undefined}
                            sx={{ width: 32, height: 32, bgcolor: stringToColor(data?.friendAlumni?.name) }}
                          >
                            {!data?.image?.data && getInitial(data?.friendAlumni?.name)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="body2">
                              {formatName(data?.friendAlumni?.name)}
                              {isExisting && (
                                <Typography component="span" variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                                  (Added)
                                </Typography>
                              )}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="textSecondary">
                              {data?.friendAlumni?.email}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    </Fragment>
                  )
                })
              )}
            </List>
          </Box>

          {totalPages > 1 && (
            <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
              <Stack spacing={1} alignItems="center">
                <Pagination count={totalPages} page={page} onChange={handlePageChange} size="small" />
                <Typography variant="caption" color="textSecondary">
                  {(page - 1) * rowsPerPage + 1} - {Math.min(page * rowsPerPage, totalCount)} of {totalCount}
                </Typography>
              </Stack>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setGroupModalOpen(false)
              setSelectedUsers([])
              setSearchTerm("")
              setPage(1)
            }}
            size="small"
          >
            Cancel
          </Button>
          <Button onClick={handleAddMembers} variant="contained" disabled={!selectedUsers.length} size="small">
            Add ({selectedUsers.length})
          </Button>
        </DialogActions>
      </Dialog>

      <UploadProfileAndCoverImage
        setPresignedImage={setPresignedImage}
        open={openProfileUpdate}
        setFilePathName={setFilePathName}
        onClose={handleCloseUpdateProfile}
      />
    </Card>
  )
}

UserDetails.propTypes = {
  user: PropTypes.object,
  groupMembers: PropTypes.array,
  groupDetailId: PropTypes.string,
  isAdmin: PropTypes.bool,
  fetchData: PropTypes.func,
  fetchGroupMembers: PropTypes.func,
  connections: PropTypes.array,
  getContactList: PropTypes.func,
  setUser: PropTypes.func,
}

export default UserDetails
